import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { QuebecObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { QuebecObstetricalFilePage, QuebecObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Quebec/QuebecObstetricalFilePage";
import { QuebecObstetricalFilePageFooter } from "@/Patient/Components/MedicalNote/Obstetric/Form/Quebec/QuebecObstetricalFilePageFooter";
import { CounterOrderComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/CounterOrderComponent/CounterOrderComponent";
import { CounterOrderModel, CounterOrderModelExtra } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/CounterOrderComponent/CounterOrderModel";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { ObstetricalCounter, ObstetricalCountersComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalCountersComponent";
import { QuebecObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Quebec/QuebecObstetricalFileFieldLimits";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { EstimatedDateOfConfinementMethod } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/EstimatedDateOfConfinementMethodEnum";
import { PrenatalVisit } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/PrenatalVisit";
import { CurrentPregnancy } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/CurrentPregnancy";
import { LifestyleAndEnvironment } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/LifestyleAndEnvironment";
import { MenstrualHistory } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/MenstrualHistory";
import { PersonalAntecedents } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/PersonalAntecedents";
import { Pregnancy } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/Pregnancy";
import { QuebecObstetricalFileFormPageOne } from "@/Patient/Models/ObstetricalFile/Quebec/PageOne/QuebecObstetricalFileFormPageOne";
import { QuebecObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFileForm";

class QuebecObstetricalFilePageOneState extends QuebecObstetricalFilePageState {
    public pageModel: QuebecObstetricalFileFormPageOne;
    public previousPregnanciesRows: Pregnancy[];
    public displayIgnoreDaysInCycle: boolean;
}

class QuebecObstetricalFilePageOne extends QuebecObstetricalFilePage<QuebecObstetricalFilePageOneState> {
    private static readonly maxPreviousPregnancyRowCount = 5;
    private readonly componentId = "obstetrical-file-page-one";
    private pendingFormUpdate = false;

    public componentDidMount(): void {
        if (this.pendingFormUpdate) {
            this.handleFormUpdate();
            this.pendingFormUpdate = false;
        }
    }

    protected buildState(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>): QuebecObstetricalFilePageOneState {
        const isNewPregnancy = props.contributionType === ObstetricalFileContributionType.NewPregnancy;
        const state = new QuebecObstetricalFilePageOneState();

        state.form = new QuebecObstetricalFileForm(props.form);
        state.pageModel = state.form.PageOne;
        state.displayIgnoreDaysInCycle = !!state.pageModel.MenstrualHistory.LastMenstrualPeriod;
        state.previousPregnanciesRows = state.pageModel.PreviousPregnancies.Pregnancies?.map(p => new Pregnancy(p)) ?? [];
        if (state.previousPregnanciesRows.length < QuebecObstetricalFilePageOne.maxPreviousPregnancyRowCount)
            state.previousPregnanciesRows.push(new Pregnancy());

        if (!state.form.PageFour.PrenatalVisits.any(p => p.ContributionId === props.contributionId) && isNewPregnancy) {
            state.form.PageFour.PrenatalVisits = [...(state.form.PageFour.PrenatalVisits ?? []), new PrenatalVisit({ ContributionId: props.contributionId, Date: props.encounter.ConsultationDate })];
            this.pendingFormUpdate = true;
        }

        return state;
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId}>
                {this.renderSpouseSection()}
                {this.renderFamilyAntecedentsSection()}
                {this.renderPersonalAntecedentsSection()}
                {this.renderMenstrualHistorySection()}
                {this.renderPreviousPregnancySection()}
                {this.renderCurrentPregnancySection()}
                {this.renderLifestyleAndEnvironmentSection()}
                {this.renderFooter()}
            </div>
        );
    }

    private renderSpouseSection(): JSX.Element {
        const spouseInfo = this.state.pageModel.SpousalInfo;
        if (!spouseInfo)
            return null;

        return (
            <div className="section section-spouse border border-top">
                <div className="row">
                    {this.renderField(
                        "NameOfSpouseLabel",
                        this.controlRenderer.renderTextbox({
                            value: spouseInfo.Name,
                            onChange: value => this.mutateModelState(s => s.pageModel.SpousalInfo.Name = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.SpouseFullName.MaxChars
                        }),
                        "col-xs-5"
                    )}
                    {this.renderField(
                        "AgeLabel",
                        this.controlRenderer.renderTextbox({
                            value: spouseInfo.Age?.toString(),
                            onChange: value => this.mutateModelState(s => s.pageModel.SpousalInfo.Age = value && Number(value)),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.SpouseAge.MaxChars,
                            className: "age-field",
                            isInteger: true
                        }),
                        "col-xs-1"
                    )}
                    {this.renderField(
                        "MedicalProblemsAndFamilyAntecedentsLabel",
                        this.controlRenderer.renderTextbox({
                            value: spouseInfo.MedicalProblemsAndFamilyAntecedents,
                            onChange: value => this.mutateModelState(s => s.pageModel.SpousalInfo.MedicalProblemsAndFamilyAntecedents = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.SpouseMedicalProblemsAndFamilyAntecedents.MaxChars
                        }),
                        "col-xs-6"
                    )}
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        {this.controlRenderer.renderTextbox({
                            value: spouseInfo.Description,
                            onChange: value => this.mutateModelState(s => s.pageModel.SpousalInfo.Description = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.SpouseDescription.MaxChars
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderFamilyAntecedentsSection(): JSX.Element {
        const remarkId = "family-antecedents-remarks";
        const clickableTextContext = QuebecObstetricalFileClickableTextContext.PageOne_FamilyAntecedents;
        const familyAntecedents = this.state.pageModel.FamilyAntecedents;
        if (!familyAntecedents)
            return null;

        const details = ["HypertensionLabel", "DiabetesLabel", "CardiopathyLabel", "MultiplePregnanciesLabel", "AnomaliesLabel", "GeneticDiseaseLabel", "ThrombophiliaLabel", "ObstetricalComplicationsLabel"];

        return (
            <div className="section section-family-antecedents border border-top">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("FamilyAntecedentsLabel")}</h4>
                        {!this.props.readonly &&
                            <p>{details.map((label, i) => <span key={`span_${label}`} className={i > 0 ? "text-lowercase" : ""}>{i > 0 ? ", " : ""}{cacheStore.GetString(label)}</span>)}</p>
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        {this.controlRenderer.renderTextarea({
                            value: familyAntecedents.Remarks,
                            onChange: value => this.mutateModelState(s => s.pageModel.FamilyAntecedents.Remarks = value),
                            id: remarkId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.FamilyAntecedents.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page1.FamilyAntecedents.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(remarkId, clickableTextContext)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderPersonalAntecedentsSection(): JSX.Element {
        const specifyId = "personal-antecedents-specify";
        const clickableTextContext = QuebecObstetricalFileClickableTextContext.PageOne_PersonalAntecedents;
        const personalAntecedents = this.state.pageModel.PersonalAntecedents;
        if (!personalAntecedents)
            return null;

        return (
            <div className="section border border-top">
                <div className="row header header-space">
                    <div className="col-xs-12">
                        <h4>
                            <span>{cacheStore.GetString("PersonalAntecedentsLabel")}</span>
                            <span className="thin-font">(<span>0 {cacheStore.GetString("NegativeLabel")}</span><span>+ {cacheStore.GetString("AbnormalLabel")}</span><span>- {cacheStore.GetString("NotApplicableFullLabel")})</span></span>
                        </h4>
                        {!this.props.readonly && (
                            <p>
                                <a onClick={() => this.handleMarkEmptyPersonalAntecedentsFieldsAsNegative()}>
                                    {cacheStore.GetString("MarkAllEmptyFieldsAsNegativeLabel")}
                                </a>
                                {this.props.previousObstetricalFile && (
                                    <a onClick={() => this.handleLoadPersonalAntecedentsFromPreviousObstetricalFile()}>
                                        {cacheStore.GetString("LoadInformationFromPreviousObstetricalFileLabel")}
                                    </a>
                                )}
                                <span className="text-italic">{cacheStore.GetString("ClickOnNameToMarkAsAbnormalLabel")}</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <CounterOrderComponent
                            models={this.getPersonalAntecedentsCounterOrderModel(personalAntecedents)}
                            counter={personalAntecedents.CounterIndex}
                            readonly={this.props.readonly}
                            onModelUpdate={(model, counter) => this.mutateModelState(s => s.pageModel.PersonalAntecedents.CounterIndex = counter, () => this.handleFormUpdate())}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("SpecifyLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: personalAntecedents.Specify,
                            onChange: value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.Specify = value),
                            id: specifyId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PersonalAntecedentsSpecify.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page1.PersonalAntecedentsSpecify.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(specifyId, clickableTextContext)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private getPersonalAntecedentsCounterOrderModel(personalAntecedents: PersonalAntecedents): CounterOrderModel[] {
        if (!personalAntecedents)
            return [];

        return [
            new CounterOrderModel("CardiacDiseaseLabel", personalAntecedents.CardiacDisease.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.CardiacDisease.Value = value)),
            new CounterOrderModel("HypertensionLabel", personalAntecedents.Hypertension.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.Hypertension.Value = value)),
            new CounterOrderModel("DVTorPELabel", personalAntecedents.DVTorPE.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.DVTorPE.Value = value)),
            new CounterOrderModel("RenalDiseaseLabel", personalAntecedents.RenalDisease.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.RenalDisease.Value = value)),
            new CounterOrderModel("DiabetesLabel", personalAntecedents.Diabetes.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.Diabetes.Value = value)),
            new CounterOrderModel("ThyroidDiseaseLabel", personalAntecedents.ThyroidDisease.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.ThyroidDisease.Value = value)),
            new CounterOrderModel("EpilepsyLabel", personalAntecedents.Epilepsy.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.Epilepsy.Value = value)),
            new CounterOrderModel("BloodDyscrasiaLabel", personalAntecedents.BloodDyscrasia.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.BloodDyscrasia.Value = value)),
            new CounterOrderModel("BloodTransfusionsLabel", personalAntecedents.BloodTransfusions.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.BloodTransfusions.Value = value)),
            new CounterOrderModel("RhABOIncompatibilityLabel", personalAntecedents.RhABOIncompatibility.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.RhABOIncompatibility.Value = value)),
            new CounterOrderModel("NeuropsychiatricDiseaseLabel", personalAntecedents.NeuropsychiatricDisease.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.NeuropsychiatricDisease.Value = value)),
            new CounterOrderModel("PreviousSurgeryLabel", personalAntecedents.PreviousSurgery.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.PreviousSurgery.Value = value)),
            new CounterOrderModel("AllergiesAsthmaLabel", personalAntecedents.AllergiesAsthma.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.AllergiesAsthma.Value = value)),
            new CounterOrderModel("HepatitisBLabel", personalAntecedents.HepatitisB.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.HepatitisB.Value = value), [
                new CounterOrderModelExtra("AcuteLabel", personalAntecedents.HepatitisB.IsAcute, isCheck => this.mutateModelState(s => s.pageModel.PersonalAntecedents.HepatitisB.IsAcute = isCheck)),
                new CounterOrderModelExtra("CarrierLabel", personalAntecedents.HepatitisB.IsCarrier, isCheck => this.mutateModelState(s => s.pageModel.PersonalAntecedents.HepatitisB.IsCarrier = isCheck))
            ]),
            new CounterOrderModel("GenitalHerpesLabel", personalAntecedents.GenitalHerpes.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.GenitalHerpes.Value = value), [
                new CounterOrderModelExtra("SpouseLabel", personalAntecedents.GenitalHerpes.IsSpouse, isCheck => this.mutateModelState(s => s.pageModel.PersonalAntecedents.GenitalHerpes.IsSpouse = isCheck))
            ]),
            new CounterOrderModel("ChickenPoxLabel", personalAntecedents.ChickenPox.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.ChickenPox.Value = value)),
            new CounterOrderModel("STBBIsLabel", personalAntecedents.STBBIs.Value, value => this.mutateModelState(s => s.pageModel.PersonalAntecedents.STBBIs.Value = value))
        ];
    }

    private renderMenstrualHistorySection(): JSX.Element {
        if (!this.state.pageModel.MenstrualHistory)
            return null;

        return (
            <div className="section menstrual-history border border-top">
                <div className="row header">
                    <h4>{cacheStore.GetString("MenstrualHistoryLabel")}</h4>
                </div>

                <div className="flex-row menstrual-history-fieldsets">
                    <div className="flex-column field-items-set">
                        <div className="field-and-label-container">
                            <div className="field-label">{`${cacheStore.GetString("CycleLabel")} (${cacheStore.GetString("DaysLabel").toLowerCase()})`}</div>

                            <div className="flex-row">
                                {this.controlRenderer.renderTextbox({
                                    value: this.state.pageModel.MenstrualHistory.DaysInCycle?.toString(),
                                    onChange: value => this.handleDaysInCycleChange(value),
                                    maxLength: QuebecObstetricalFileFieldLimits.Page1.MenstrualHistoryLengthInDays.MaxChars,
                                    isNumeric: true,
                                    className: "cycle-textbox"
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex-column field-items-set">
                        <div className="field-and-label-container">
                            <div className="field-label">{cacheStore.GetString("DateOfLastMenstrualPeriodAcronymLabel")}</div>
                            <div className="field-value-inline">
                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.MenstrualHistory.LastMenstrualPeriod,
                                    onChange: value => this.handleLastMenstrualPeriodChange(value),
                                    id: "DMLPDateSelector"
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex-column field-items-set">
                        <div className="flex-column">
                            <div className="field-and-label-container">
                                <div className="field-label">{cacheStore.GetString("EstimatedDateOfConfinementAcronymLabel")}</div>
                                <div className="field-value-inline">
                                    {this.controlRenderer.renderDateSelector({
                                        value: this.state.pageModel.MenstrualHistory.EstimatedDateOfConfinement,
                                        onChange: value => this.handleEstimatedDateOfConfinementChange(value),
                                        id: "EDCDateSelector"
                                    })}
                                </div>
                            </div>

                            {!this.props.readonly && this.state.displayIgnoreDaysInCycle && (
                                <div className="ignoreDaysInCycle-checkbox margin-xs-top">
                                    <CheckboxControl
                                        checked={this.state.pageModel.MenstrualHistory.IgnoreDaysInCycle}
                                        label={cacheStore.GetString("IgnoreNumberOfDaysInCycleLabel")}
                                        onChange={() => this.handleIgnoreDaysInCycleChange()}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex-column field-items-set">
                        <div className="field-and-label-container">
                            <div className="field-label">{cacheStore.GetString("PositivePregnancyTestOnLabel")}</div>
                            <div className="field-value-inline">
                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.MenstrualHistory.PositivePregnancyTestOn,
                                    onChange: value => this.mutateModelState(s => s.pageModel.MenstrualHistory.PositivePregnancyTestOn = value, () => this.handleFormUpdate()),
                                    className: "PositivePregnancyTestDateSelector"
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex-column field-items-set">
                        <div className="field-and-label-container">
                            <div className="field-label">{cacheStore.GetString("EmbryoTransferDateLabel")}</div>
                            <div className="field-value-inline">
                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.MenstrualHistory.EmbryoTransferDate,
                                    onChange: value => this.mutateModelState(s => s.pageModel.MenstrualHistory.EmbryoTransferDate = value, () => this.handleFormUpdate()),
                                    id: "EmbryoTransferDateSelector"
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPreviousPregnancySection(): JSX.Element {
        const remarkId = "previous-pregnancy-remark";
        const clickableTextContext = QuebecObstetricalFileClickableTextContext.PageOne_PreviousPregnancies;
        const obstetricalSummary = this.state.form.ObstetricalCounters;
        const previousPregnancies = this.state.pageModel.PreviousPregnancies;
        if (!previousPregnancies)
            return null;

        return (
            <div className="section border border-top previous-pregnancies">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("PreviousPregnanciesLabel")}</h4>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12 pregnancies-counter-container">
                        <div className="bold">{cacheStore.GetString("TotalNumberOfPreviousPregnanciesLabel")}</div>
                        <div className="pregnancies-counter">
                            <ObstetricalCountersComponent
                                readonly={this.props.readonly}
                                obstetricalCounters={obstetricalSummary}
                                onChange={counters => this.mutateModelState(s => { s.form.ObstetricalCounters = counters; })}
                                onBlur={() => this.handleObstetricalCountersUpdate()}
                                containerId="quebec-page1-counters"
                                longLabel
                                includeFields={[
                                    ObstetricalCounter.Term,
                                    ObstetricalCounter.Premature,
                                    ObstetricalCounter.Abortion,
                                    ObstetricalCounter.Live
                                ]}
                                alternateLabels={{ top: { [ObstetricalCounter.Live]: "LivingChildrenLabel" } }}
                            />
                        </div>
                    </div>
                    <div className="col-xs-12">
                        <table>
                            <thead>
                                <tr>
                                    <th rowSpan={2} className="text-bold col-small">{cacheStore.GetString("NumberShortLabel")}</th>
                                    <th rowSpan={2} className="text-bold col-medium">{cacheStore.GetString("DateLabel")}</th>
                                    <th rowSpan={2} className="text-bold">{cacheStore.GetString("PlaceLabel")}</th>
                                    <th rowSpan={2} className="col-medium"><strong>{cacheStore.GetString("DurationOfPregnancyLabel")}</strong> <span className="text-lowercase">({cacheStore.GetString("WeeksAbbreLabel")})</span></th>
                                    <th rowSpan={2} className="col-medium"><strong>{cacheStore.GetString("DurationOfLabourLabel")}</strong> <span className="text-lowercase">({cacheStore.GetString("HourAbbreLabel")})</span></th>
                                    <th rowSpan={2} className="text-bold">{cacheStore.GetString("MethodOfDeliveryLabel")}</th>
                                    <th rowSpan={2} className="text-bold">{cacheStore.GetString("TypeOfAnesthesiaLabel")}</th>
                                    <th rowSpan={2} className="text-bold col-medium"><span className="myle-icon-24 male icon" /><span className="text-lowercase"> {cacheStore.GetString("OrLabel")} </span><span className="myle-icon-24 female icon" /></th>
                                    <th rowSpan={2} className="col-medium"><strong>{cacheStore.GetString("MassLabel")}</strong><br />{this.renderWeightUnit(previousPregnancies.MassUnit, unit => this.handleOnMassUnitChange(unit))}</th>
                                    <th colSpan={2} className="text-bold">{cacheStore.GetString("ParticularitiesLabel")}</th>
                                </tr>
                                <tr>
                                    <th>{cacheStore.GetString("AtMotherLabel")}</th>
                                    <th>{cacheStore.GetString("AtChildLabel")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.previousPregnanciesRows.map(p => this.renderPreviousPregnancyRow(p))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("RemarksLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: previousPregnancies.Remarks,
                            onChange: value => this.mutateModelState(s => s.pageModel.PreviousPregnancies.Remarks = value),
                            id: remarkId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesRemarks.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesRemarks.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(remarkId, clickableTextContext)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderPreviousPregnancyRow(pregnancy: Pregnancy): JSX.Element {
        if (!pregnancy)
            return null;

        return (
            <tr key={pregnancy.Id}>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: pregnancy.Number?.toString(),
                            onChange: value => {
                                if (!pregnancy.hasValue())
                                    this.mutateModelState(s => s.previousPregnanciesRows = s.previousPregnanciesRows.mutate(p => p.Number = value && Number(value), p => p.Id === pregnancy.Id));
                                else
                                    this.mutatePreviousPregnancyRowState(pregnancy.Id, s => s.Number = value && Number(value), () => this.handleFormUpdate());
                            },
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableNo.MaxChars,
                            isNumeric: true,
                            isInteger: true,
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderDateWithPrecision({
                            date: pregnancy.Date,
                            precision: pregnancy.DatePrecision,
                            onChange: dateWithPrecision => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => this.handlePreviousPregnanciesDate(p, dateWithPrecision), () => this.handleFormUpdate()),
                            id: `date_${pregnancy.Id}`,
                            className: "table-date-selector"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: pregnancy.Place,
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.Place = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTablePlace.MaxChars,
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        <GestationalAgeComponent
                            id={`durationOfPregnancy_${pregnancy.Id}`}
                            gestationalAge={pregnancy.DurationOfPregnancyInWeeks}
                            readonly={this.props.readonly}
                            onChange={value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.DurationOfPregnancyInWeeks = value)}
                            controlRenderer={this.controlRenderer}
                        />
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: pregnancy.DurationOfLabourInHours,
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.DurationOfLabourInHours = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableDurationOfLabourInHours.MaxChars,
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: pregnancy.MethodOfDelivery,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(cacheStore.GetString("SVDLabel"), "SVD"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("CSectionLabel"), "CSection"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("ODVacuumLabel"), "ODVacuum"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("OBForcepsLabel"), "OBForceps"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("VaginalBirthAfterCesareanAbbrevLabel"), "VBAC"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("PosteriorVitreousDetachmentAbbrevLabel"), "PVD"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("TherapeuticAbortionAbbrevLabel"), "TAB"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("SpontaneousAbortionAbbrevLabel"), "SAB")
                                ],
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.MethodOfDelivery = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.MethodOfDelivery = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableMethodOfDelivery.MaxChars,
                            className: "table-textbox",
                            listWidth: "100%",
                            disableMatchAutoSorting: true
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: pregnancy.TypeOfAnesthesia,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(cacheStore.GetString("NoneWordLabel"), "None"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("EpiduralLabel"), "Epidural"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("IntravenousLabel"), "Intravenous"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("PudendalBlockShortLabel"), "PudendalBlock"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("AnestheticGasShortLabel"), "AnestheticGas")
                                ],
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.TypeOfAnesthesia = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.TypeOfAnesthesia = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableTypeOfAnesthesia.MaxChars,
                            className: "table-textbox",
                            listWidth: "120px",
                            disableMatchAutoSorting: true
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: pregnancy.Gender,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(cacheStore.GetString("MaleAbbrevLabel"), "M"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("FemaleAbbrevLabel"), "F")
                                ],
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.Gender = value),
                            onAutoCompleteSelected: code => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.Gender = code),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableMaleOrFemale.MaxChars,
                            className: "table-textbox",
                            listWidth: "100%",
                            disableMatchAutoSorting: true
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: pregnancy.getMassString(),
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.Mass = value && Number(value)),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableMassInKg.MaxChars,
                            className: "table-textbox",
                            isNumeric: true
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: pregnancy.ParticularityMother,
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.ParticularityMother = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableParticularitiesMother.MaxChars,
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: pregnancy.ParticularityChild,
                            onChange: value => this.mutatePreviousPregnancyRowState(pregnancy.Id, p => p.ParticularityChild = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.PreviousPregnanciesTableParticularitiesChild.MaxChars,
                            className: "table-textbox"
                        })}
                    </div>
                </td>
            </tr>
        );
    }

    private renderCurrentPregnancySection(): JSX.Element {
        const remarksId = "current-pregnancy-remarks";
        const clickableTextContext = QuebecObstetricalFileClickableTextContext.PageOne_CurrentPregnancy;
        const currentPregnancy = this.state.pageModel.CurrentPregnancy;
        if (!currentPregnancy)
            return null;

        return (
            <div className="section border border-top">
                <div className="row header header-space">
                    <div className="col-xs-12">
                        <h4>
                            <span>{cacheStore.GetString("CurrentPregnancyLabel")} ({cacheStore.GetString("ReviewOfSystemsLabel").toLowerCase()})</span>
                        </h4>
                        {!this.props.readonly && (
                            <p>
                                <a onClick={() => this.handleMarkCurrentPregnancyFieldsAsNegative()}>
                                    {cacheStore.GetString("MarkAllEmptyFieldsAsNegativeLabel")}
                                </a>
                                <span className="text-italic">{cacheStore.GetString("ClickOnNameToMarkAsAbnormalLabel")}</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <CounterOrderComponent
                            models={this.getCurrentPregnancyCounterOrderModel(currentPregnancy)}
                            counter={currentPregnancy.CounterIndex}
                            readonly={this.props.readonly}
                            onModelUpdate={(model, counter) => this.mutateModelState(s => s.pageModel.CurrentPregnancy.CounterIndex = counter, () => this.handleFormUpdate())}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("RemarksLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: currentPregnancy.Remarks,
                            onChange: value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.Remarks = value),
                            id: remarksId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.CurrentPregnancyRemarks.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page1.CurrentPregnancyRemarks.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(remarksId, clickableTextContext)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private getCurrentPregnancyCounterOrderModel(currentPregnancy: CurrentPregnancy): CounterOrderModel[] {
        if (!currentPregnancy)
            return [];

        return [
            new CounterOrderModel("NauseaAndVomitingLabel", currentPregnancy.NauseaAndVomiting.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.NauseaAndVomiting.Value = value)),
            new CounterOrderModel("ConstipationLabel", currentPregnancy.Constipation.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.Constipation.Value = value)),
            new CounterOrderModel("RGOLabel", currentPregnancy.RGO.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.RGO.Value = value)),
            new CounterOrderModel("EdemaLabel", currentPregnancy.Edema.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.Edema.Value = value)),
            new CounterOrderModel("HeadachesLabel", currentPregnancy.Headaches.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.Headaches.Value = value)),
            new CounterOrderModel("VaginalDischargeLabel", currentPregnancy.VaginalDischarge.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.VaginalDischarge.Value = value)),
            new CounterOrderModel("BleedingLabel", currentPregnancy.Bleeding.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.Bleeding.Value = value)),
            new CounterOrderModel("AbdominalPainLabel", currentPregnancy.AbdominalPain.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.AbdominalPain.Value = value)),
            new CounterOrderModel("UrinarySymptomsLabel", currentPregnancy.UrinarySymptoms.Value, value => this.mutateModelState(s => s.pageModel.CurrentPregnancy.UrinarySymptoms.Value = value))
        ];
    }

    private renderLifestyleAndEnvironmentSection(): JSX.Element {
        const specifyId = "lifestyleAndEnvironment-specify";
        const clickableTextContext = QuebecObstetricalFileClickableTextContext.PageOne_LifestyleAndEnviroment;
        const lifestyleAndEnvironment = this.state.pageModel.LifestyleAndEnvironment;
        if (!lifestyleAndEnvironment)
            return null;

        return (
            <div className="section border border-top">
                <div className="row header header-space">
                    <div className="col-xs-12">
                        <h4>
                            <span>{cacheStore.GetString("LifestyleAndEnvironmentLabel")}</span>
                        </h4>
                        {!this.props.readonly && (
                            <p>
                                <a onClick={() => this.handleMarkEmptyLifestyleAndEnvironmentFieldsAsNegative()}>
                                    {cacheStore.GetString("MarkAllEmptyFieldsAsNegativeLabel")}
                                </a>
                                <span className="text-italic">{cacheStore.GetString("ClickOnNameToMarkAsAbnormalLabel")}</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <CounterOrderComponent
                            models={this.getLifestyleAndEnvironmentCounterOrderModel(lifestyleAndEnvironment)}
                            counter={lifestyleAndEnvironment.CounterIndex}
                            readonly={this.props.readonly}
                            onModelUpdate={(model, counter) => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.CounterIndex = counter, () => this.handleFormUpdate())}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("RemarksLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: lifestyleAndEnvironment.Remarks,
                            onChange: value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Remarks = value),
                            id: specifyId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page1.LifestyleAndEnvironmentRemarks.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page1.LifestyleAndEnvironmentRemarks.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(specifyId, clickableTextContext)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private getLifestyleAndEnvironmentCounterOrderModel(lifestyleAndEnvironment: LifestyleAndEnvironment): CounterOrderModel[] {
        if (!lifestyleAndEnvironment)
            return [];

        return [
            new CounterOrderModel("RadiationXRaysLabel", lifestyleAndEnvironment.RadiationXRays.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.RadiationXRays.Value = value)),
            new CounterOrderModel("TraumaLabel", lifestyleAndEnvironment.Trauma.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Trauma.Value = value)),
            new CounterOrderModel("ContactCatLabel", lifestyleAndEnvironment.ContactCat.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.ContactCat.Value = value)),
            new CounterOrderModel("FeedingLabel", lifestyleAndEnvironment.Feeding.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Feeding.Value = value)),
            new CounterOrderModel("MedicationLabel", lifestyleAndEnvironment.Medication.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Medication.Value = value)),
            new CounterOrderModel("VaccinationLabel", lifestyleAndEnvironment.Vaccination.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Vaccination.Value = value)),
            new CounterOrderModel("DrugsLabel", lifestyleAndEnvironment.Drugs.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Drugs.Value = value)),
            new CounterOrderModel("AlcoholLabel", lifestyleAndEnvironment.Alcohol.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Alcohol.Value = value)),
            new CounterOrderModel("CigarettesNumberLabel", lifestyleAndEnvironment.Cigarettes.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Cigarettes.Value = value)),
            new CounterOrderModel("CoffeeLabel", lifestyleAndEnvironment.Coffee.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Coffee.Value = value)),
            new CounterOrderModel("FolicAcidSupplementLabel", lifestyleAndEnvironment.FolicAcidSupplement.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.FolicAcidSupplement.Value = value)),
            new CounterOrderModel("SocioculturalLabel", lifestyleAndEnvironment.Sociocultural.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Sociocultural.Value = value)),
            new CounterOrderModel("EthnoculturalLabel", lifestyleAndEnvironment.Ethnocultural.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Ethnocultural.Value = value)),
            new CounterOrderModel("RiskOfInfectionLabel", lifestyleAndEnvironment.RiskOfInfection.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.RiskOfInfection.Value = value)),
            new CounterOrderModel("OccupationLabel", lifestyleAndEnvironment.Occupation.Value, value => this.mutateModelState(s => s.pageModel.LifestyleAndEnvironment.Occupation.Value = value))
        ];
    }

    private renderFooter(): JSX.Element {
        if (!this.state.pageModel.Signature)
            return null;

        return (
            <QuebecObstetricalFilePageFooter
                signature={this.state.pageModel.Signature}
                isInformationShared={this.state.pageModel.IsDownSyndromeInformationShared}
                informationLabel="QuebecPrenatalDownSymdromeScreeningProgramLeafletLabel"
                readonly={this.props.readonly}
                onInformationSharedChange={isChecked => this.mutateModelState(s => s.pageModel.IsDownSyndromeInformationShared = isChecked)}
                onSignatureChange={value => this.mutateModelState(s => s.pageModel.Signature.Value = value, null, true)}
                onDateChange={date => this.mutateModelState(s => s.pageModel.Signature.Date = date, () => this.handleFormUpdate())}
                onFieldBlur={() => this.handleFormUpdate()}
            />
        );
    }

    private handleMarkEmptyPersonalAntecedentsFieldsAsNegative(): void {
        this.mutateModelState(
            s => s.pageModel.PersonalAntecedents.setEmptyCommonValueFields("0"),
            () => this.handleFormUpdate()
        );
    }

    private handleLoadPersonalAntecedentsFromPreviousObstetricalFile(): void {
        this.mutateModelState(
            s => s.pageModel.PersonalAntecedents = this.props.previousObstetricalFile.getFormObject<QuebecObstetricalFileForm>().PageOne.PersonalAntecedents,
            () => this.handleFormUpdate()
        );
    }

    private handleMarkCurrentPregnancyFieldsAsNegative(): void {
        this.mutateModelState(
            s => s.pageModel.CurrentPregnancy.setEmptyCommonValueFields("0"),
            () => this.handleFormUpdate()
        );
    }

    private handleMarkEmptyLifestyleAndEnvironmentFieldsAsNegative(): void {
        this.mutateModelState(
            s => s.pageModel.LifestyleAndEnvironment.setEmptyCommonValueFields("0"),
            () => this.handleFormUpdate()
        );
    }

    private handleEstimatedDateOfConfinementChange(value: Date): void {
        this.mutateModelState(s => {
            s.pageModel.MenstrualHistory.EstimatedDateOfConfinement = value;
            s.form.PageFour.EstimatedDateOfConfinementDateOfLastMenstruation = value;

            if (s.form.PageFour.EstimatedDateOfConfinementMethod == null)
                s.form.PageFour.EstimatedDateOfConfinementMethod = EstimatedDateOfConfinementMethod.DateOfLastMenstruation;

            this.updateGestationalAge(s);
        }, () => this.handleFormUpdate());
    }

    private handleDaysInCycleChange(value: string): void {
        this.mutateModelState(s => {
            s.pageModel.MenstrualHistory.DaysInCycle = value && Number(value);
            this.calculateEstimatedDateOfConfinement(s);
        });
    }

    private handleLastMenstrualPeriodChange(value: Date): void {
        this.mutateModelState(s => {
            s.displayIgnoreDaysInCycle = !!value;
            s.pageModel.MenstrualHistory.LastMenstrualPeriod = value;
            this.calculateEstimatedDateOfConfinement(s);
        }, () => this.handleFormUpdate());
    }

    private handleIgnoreDaysInCycleChange(): void {
        this.mutateModelState(s => {
            s.pageModel.MenstrualHistory.IgnoreDaysInCycle = !s.pageModel.MenstrualHistory.IgnoreDaysInCycle;
            this.calculateEstimatedDateOfConfinement(s);
        }, () => this.handleFormUpdate());
    }

    private calculateEstimatedDateOfConfinement(state: QuebecObstetricalFilePageOneState): void {
        if (!this.shouldCalculateEstimatedDateOfConfinement(state.pageModel.MenstrualHistory))
            return;

        const daysInCycle = state.pageModel.MenstrualHistory.IgnoreDaysInCycle ? null : +state.pageModel.MenstrualHistory.DaysInCycle;
        const estimatedDateOfConfinement = ObstetricsCalculator.calculateDateOfBirthBasedOnLastMenstrualPeriod(state.pageModel.MenstrualHistory.LastMenstrualPeriod, daysInCycle);

        state.pageModel.MenstrualHistory.EstimatedDateOfConfinement = estimatedDateOfConfinement;
        state.form.PageFour.EstimatedDateOfConfinementDateOfLastMenstruation = estimatedDateOfConfinement;

        if (state.form.PageFour.EstimatedDateOfConfinementMethod == null) {
            state.form.PageFour.EstimatedDateOfConfinementMethod = EstimatedDateOfConfinementMethod.DateOfLastMenstruation;
        }

        this.updateGestationalAge(state);
    }

    private updateGestationalAge(state: QuebecObstetricalFilePageOneState): void {
        if (EnumHelper.getValue(EstimatedDateOfConfinementMethod, state.form.PageFour.EstimatedDateOfConfinementMethod) !== EstimatedDateOfConfinementMethod.DateOfLastMenstruation)
            return;

        state.form.PageFour.updateWeeksOfGestation(this.props.contributionId);
    }

    private shouldCalculateEstimatedDateOfConfinement(menstrualHistory: MenstrualHistory): boolean {
        if (!menstrualHistory.LastMenstrualPeriod || (!menstrualHistory.IgnoreDaysInCycle && !menstrualHistory.DaysInCycle))
            return false;

        return true;
    }

    private mutatePreviousPregnancyRowState(id: number, mutation: (state: Pregnancy) => void, then?: () => void): void {
        let pregnanciesWereDeleted = false;
        this.mutateModelState(
            s => {
                s.previousPregnanciesRows = s.previousPregnanciesRows.mutate(
                    p => {
                        mutation(p);
                        if (!p.Number)
                            p.Number = s.previousPregnanciesRows.indexOf(p) + 1;
                    },
                    p => p.Id === id
                );

                pregnanciesWereDeleted = this.removeEmptyPregnanciesExceptLast(s.previousPregnanciesRows);

                if (pregnanciesWereDeleted && !s.previousPregnanciesRows[s.previousPregnanciesRows.length - 1].hasValue())
                    s.previousPregnanciesRows[s.previousPregnanciesRows.length - 1].Number = null;

                if (s.previousPregnanciesRows.length < QuebecObstetricalFilePageOne.maxPreviousPregnancyRowCount && s.previousPregnanciesRows[s.previousPregnanciesRows.length - 1].hasValue())
                    s.previousPregnanciesRows.push(new Pregnancy());

                s.pageModel.PreviousPregnancies.Pregnancies = s.previousPregnanciesRows
                    .filter((p, idx) => idx !== s.previousPregnanciesRows.length - 1 || p.hasValue())
                    .map(p => new Pregnancy(p));
            },
            () => {
                if (pregnanciesWereDeleted) {
                    this.handleFormUpdate();
                }
                then?.();
            }
        );
    }

    private mutateModelState(mutation: (state: QuebecObstetricalFilePageOneState) => void, then?: () => void, ignoreSignature?: boolean): void {
        this.mutateState(s => {
            mutation(s);

            const currentUser = sessionStore.getCurrentEmployee();
            if (s.pageModel.Signature.UserContactFacetIds.any(id => id === currentUser.ContactFacetId))
                return;

            s.pageModel.Signature.UserContactFacetIds.push(currentUser.ContactFacetId);
            s.pageModel.Signature.Date ||= this.props.encounter.ConsultationDate;

            if (!ignoreSignature)
                s.pageModel.Signature.Value = limitTextLength((`${s.pageModel.Signature.Value}${(!!s.pageModel.Signature.Value ? ", " : "")}${currentUser.nameWithPrefix()}`), 65);
        }, then);
    }

    private handlePreviousPregnanciesDate(pregnancy: Pregnancy, dateWithPrecision: DateTimeWithPrecision): void {
        if (dateWithPrecision.value) {
            pregnancy.Date = LuxonDateTimeParser.parse(dateWithPrecision.value).toJSDate();
            pregnancy.DatePrecision = dateWithPrecision.precision;
        }
        else if (IsNullOrWhiteSpace(dateWithPrecision.text))
            pregnancy.Date = null;
    }

    /**
     * Removes all empty pregnancies from the tail of the list except the last pregnancy.
     * Returns whether pregnancies were deleted.
     */
    private removeEmptyPregnanciesExceptLast(pregnancies: Pregnancy[]): boolean {
        if (!pregnancies?.any())
            return false;

        const lastItemIndex = pregnancies.length - 1;
        let firstItemWithValueIndex = lastItemIndex;

        while (firstItemWithValueIndex >= 0 && !pregnancies[firstItemWithValueIndex].hasValue()) {
            firstItemWithValueIndex--;
        }

        // do not count the last row as a row to be removed. That is where the 1 constant comes from.
        const skipLastRow = 1;

        const deleteCount = lastItemIndex - firstItemWithValueIndex - skipLastRow;

        if (deleteCount > 0)
            pregnancies.splice(firstItemWithValueIndex + 1, deleteCount);

        return deleteCount > 0;
    }

    private handleOnMassUnitChange(unit: WeightUnit): void {
        if (this.state.pageModel.PreviousPregnancies.MassUnit === unit)
            return;

        this.mutateModelState(
            s => {
                const fromUnit = s.pageModel.PreviousPregnancies.MassUnit;
                s.pageModel.PreviousPregnancies.setMassUnit(unit);
                s.previousPregnanciesRows.forEach(p => p.convertMass(fromUnit, unit));
            },
            () => this.handleFormUpdate()
        );
    }
}

export { QuebecObstetricalFilePageOne };
